import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { withCsrfHeader } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Check, X, Sparkles, Users, Star, Zap,
  Crown, Rocket, Gem, Building2, Briefcase, Award, Shield, type LucideIcon,
} from "lucide-react";

type SignupAudience = "individual" | "team_enterprise" | null;

// Map admin-selected icon keys to lucide-react components. Anything not in this
// map falls back to Sparkles so a typo never crashes the page.
const ICON_MAP: Record<string, LucideIcon> = {
  Zap, Star, Users, Sparkles, Crown, Rocket, Gem, Building2, Briefcase, Award, Shield,
};

function resolveIcon(name: string | null | undefined): LucideIcon {
  if (!name) return Sparkles;
  return ICON_MAP[name] || Sparkles;
}

interface SignupTierFeature {
  name: string;
  included: boolean;
  isHeader?: boolean;
  indent?: boolean;
}

interface SignupTier {
  name: string;
  displayName: string | null;
  description: string | null;
  audience: SignupAudience;
  monthlyPrice: number | null; // cents
  yearlyPrice: number | null; // cents
  maxUsers: number | null;
  maxTalentProfiles: number | null;
  pricePerAdditionalUser: number | null; // cents
  marketingIcon: string | null;
  marketingHighlight: string | null;
  marketingPopular: boolean;
  features: SignupTierFeature[];
}

type AudienceTab = "individual" | "team_enterprise";

export default function Signup() {
  const [step, setStep] = useState(1);
  const [selectedTier, setSelectedTier] = useState("pro");
  const [isAnnual, setIsAnnual] = useState(true);
  const [audienceTab, setAudienceTab] = useState<AudienceTab>("individual");
  const [workspaceName, setWorkspaceName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: signupTiers, isLoading: tiersLoading } = useQuery<SignupTier[]>({
    queryKey: ["/api/signup/tiers"],
  });

  // Sort so the "Most Popular" / popular tier sits in the middle of the row, then by price.
  const sortedTiers = useMemo<SignupTier[]>(() => {
    if (!signupTiers) return [];
    return [...signupTiers].sort((a, b) => {
      // Untagged tiers (audience === null) appear under both tabs but stay at the end.
      const audienceRank = (t: SignupTier) =>
        t.audience === "individual" ? 0 : t.audience === "team_enterprise" ? 1 : 2;
      const ra = audienceRank(a);
      const rb = audienceRank(b);
      if (ra !== rb) return ra - rb;
      const pa = a.monthlyPrice ?? Number.MAX_SAFE_INTEGER;
      const pb = b.monthlyPrice ?? Number.MAX_SAFE_INTEGER;
      return pa - pb;
    });
  }, [signupTiers]);

  const visibleTiers = useMemo(() => {
    return sortedTiers.filter((t) => {
      // Untagged tiers (null audience) appear under both tabs.
      if (t.audience == null) return true;
      return t.audience === audienceTab;
    });
  }, [sortedTiers, audienceTab]);

  useEffect(() => {
    if (visibleTiers.length === 0) return;
    if (!visibleTiers.find((t) => t.name === selectedTier)) {
      const popular = visibleTiers.find((t) => t.marketingPopular) ?? visibleTiers[0];
      setSelectedTier(popular.name);
    }
  }, [visibleTiers, selectedTier]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    const slug = workspaceName.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    
    try {
      const res = await fetch("/api/signup", {
        method: "POST",
        headers: await withCsrfHeader({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify({
          workspaceName,
          slug,
          tier: selectedTier,
          billingCycle: isAnnual ? "annual" : "monthly",
          email,
          password,
          couponCode: couponCode.trim() || undefined,
        }),
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.message || "Signup failed");
      }
      
      // Redirect to Stripe checkout
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error: any) {
      setError(error.message || "Signup failed. Please try again.");
      setIsLoading(false);
    }
  };

  const selectedTierData = sortedTiers.find((t) => t.name === selectedTier);
  // Convert cents → dollars for the friendly display strings.
  const formatDollars = (cents: number | null | undefined) =>
    cents == null ? 0 : Math.round(cents / 100);


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      {step === 1 ? (
        <div className="py-12 px-4">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-[hsl(var(--green))] mb-4">
              Choose Your Plan
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Run the show, not the spreadsheet. Pick the plan that fits today — upgrade as the calendar fills.
            </p>
            
            {/* Audience Toggle */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center p-1 bg-muted rounded-full" data-testid="segmented-audience">
                <button
                  type="button"
                  onClick={() => setAudienceTab("individual")}
                  className={`text-sm font-medium px-5 py-2 rounded-full transition-colors ${
                    audienceTab === "individual"
                      ? "bg-background shadow-sm text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="pill-audience-individual"
                  aria-pressed={audienceTab === "individual"}
                >
                  Individual
                </button>
                <button
                  type="button"
                  onClick={() => setAudienceTab("team_enterprise")}
                  className={`text-sm font-medium px-5 py-2 rounded-full transition-colors ${
                    audienceTab === "team_enterprise"
                      ? "bg-background shadow-sm text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="pill-audience-team"
                  aria-pressed={audienceTab === "team_enterprise"}
                >
                  Team and Enterprise
                </button>
              </div>
            </div>

            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-4 p-2 bg-muted rounded-full">
              <span className={`text-sm font-medium px-3 py-1 rounded-full transition-colors ${!isAnnual ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>
                Monthly
              </span>
              <Switch
                checked={isAnnual}
                onCheckedChange={setIsAnnual}
                data-testid="toggle-billing-cycle"
              />
              <span className={`text-sm font-medium px-3 py-1 rounded-full transition-colors ${isAnnual ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>
                Annual
              </span>
              {isAnnual && (
                <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                  Save up to 17%
                </Badge>
              )}
            </div>
          </div>

          {/* Pricing Cards */}
          {tiersLoading ? (
            <div
              className="max-w-7xl mx-auto text-center text-muted-foreground py-12"
              data-testid="text-tiers-loading"
            >
              Loading plans…
            </div>
          ) : visibleTiers.length === 0 ? (
            <div
              className="max-w-2xl mx-auto text-center text-muted-foreground py-12"
              data-testid="text-tiers-empty"
            >
              No plans are available for this audience yet. Please check back soon
              or <a href="mailto:admin@entertaincrm.com" className="text-blue-500 hover:underline">contact us</a>.
            </div>
          ) : (
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {visibleTiers.map((tier) => {
              const TierIcon = resolveIcon(tier.marketingIcon);
              const monthlyDollars = formatDollars(tier.monthlyPrice);
              const annualDollars = formatDollars(tier.yearlyPrice);
              const displayPrice = isAnnual ? annualDollars : monthlyDollars;
              const perMonthPrice = isAnnual ? Math.round(annualDollars / 12) : monthlyDollars;
              const savings = isAnnual ? (monthlyDollars * 12) - annualDollars : 0;
              const additionalUserPrice = formatDollars(tier.pricePerAdditionalUser);
              const label = tier.displayName || tier.name;
              const usersIncluded = tier.maxUsers ?? 1;
              const talentIncluded = tier.maxTalentProfiles;
              return (
                <div
                  key={tier.name}
                  className={`relative rounded-2xl transition-all duration-300 cursor-pointer group ${
                    selectedTier === tier.name 
                      ? 'ring-2 ring-blue-500 shadow-xl scale-[1.02]' 
                      : 'hover:shadow-lg hover:scale-[1.01]'
                  } ${tier.marketingPopular ? 'lg:-mt-4 lg:mb-4' : ''}`}
                  onClick={() => setSelectedTier(tier.name)}
                  data-testid={`tier-option-${tier.name}`}
                >
                  {/* Popular/Highlight Badge */}
                  {tier.marketingHighlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                      <Badge className={`px-4 py-1 text-xs font-semibold shadow-md ${
                        tier.marketingPopular 
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-0' 
                          : 'bg-gradient-to-r from-amber-400 to-orange-500 text-white border-0'
                      }`}
                      data-testid={`badge-tier-highlight-${tier.name}`}
                      >
                        {tier.marketingHighlight}
                      </Badge>
                    </div>
                  )}
                  
                  <Card className={`h-full overflow-hidden ${
                    tier.marketingPopular 
                      ? 'bg-gradient-to-b from-blue-50 to-white dark:from-blue-950/30 dark:to-gray-900 border-blue-200 dark:border-blue-800' 
                      : ''
                  }`}>
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`p-2 rounded-lg ${
                          tier.marketingPopular 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-muted'
                        }`}>
                          <TierIcon className="w-5 h-5" />
                        </div>
                        <CardTitle className="text-xl capitalize" data-testid={`text-tier-label-${tier.name}`}>
                          {label}
                        </CardTitle>
                      </div>
                      {tier.description && (
                        <p className="text-sm text-muted-foreground" data-testid={`text-tier-description-${tier.name}`}>
                          {tier.description}
                        </p>
                      )}
                    </CardHeader>
                    
                    <CardContent className="space-y-6">
                      {/* Pricing */}
                      <div>
                        <div className="flex items-baseline gap-1">
                          <span
                            className="text-4xl font-bold"
                            data-testid={`text-tier-price-${tier.name}`}
                          >
                            ${perMonthPrice}
                          </span>
                          <span className="text-muted-foreground">/mo</span>
                        </div>
                        {isAnnual ? (
                          <div className="mt-1 space-y-1">
                            <p className="text-sm text-muted-foreground">
                              Billed ${displayPrice}/year
                            </p>
                            {savings > 0 && (
                              <p className="text-sm text-green-600 font-medium">
                                Save ${savings}/year
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground mt-1">
                            Billed monthly
                          </p>
                        )}
                      </div>

                      {/* Users & Talent */}
                      <div className="py-3 px-4 rounded-lg bg-muted/50 space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">
                            {tier.maxUsers == null
                              ? 'Unlimited users'
                              : `${usersIncluded} user${usersIncluded === 1 ? '' : 's'} included`}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Star className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">
                            {talentIncluded == null || talentIncluded === -1
                              ? 'Unlimited talents'
                              : `${talentIncluded} talent${talentIncluded === 1 ? '' : 's'}`}
                          </span>
                        </div>
                        {additionalUserPrice > 0 ? (
                          <p className="text-xs text-muted-foreground mt-2">
                            +${additionalUserPrice}/user/month for additional users
                          </p>
                        ) : (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 font-medium">
                            Additional users not available
                          </p>
                        )}
                      </div>

                      {/* Features List */}
                      {tier.features.length > 0 && (
                        <ul className="space-y-2.5" data-testid={`list-tier-features-${tier.name}`}>
                          {tier.features.map((feature, idx) => (
                            <li 
                              key={idx} 
                              className={`flex items-start gap-2 text-sm ${
                                feature.isHeader ? 'font-semibold text-foreground pt-1' : ''
                              } ${feature.indent ? 'pl-4' : ''}`}
                            >
                              {!feature.isHeader && (
                                feature.included ? (
                                  <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                                ) : (
                                  <X className="w-4 h-4 text-muted-foreground/40 mt-0.5 shrink-0" />
                                )
                              )}
                              <span className={feature.included ? '' : 'text-muted-foreground/60'}>
                                {feature.name}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Select Button */}
                      <Button 
                        className={`w-full ${
                          selectedTier === tier.name
                            ? tier.marketingPopular 
                              ? 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600'
                              : ''
                            : tier.marketingPopular
                              ? 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600'
                              : 'variant-outline'
                        }`}
                        variant={selectedTier === tier.name || tier.marketingPopular ? "default" : "outline"}
                      >
                        {selectedTier === tier.name ? (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Selected
                          </>
                        ) : (
                          "Select Plan"
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
          )}

          {/* Continue Button — only render when there's a real tier to continue with.
              Without this guard, an admin who has hidden every public tier could still
              click through to step 2 with no plan selected and hit a server-side error. */}
          {visibleTiers.length > 0 && selectedTierData ? (
            <div className="max-w-md mx-auto mt-12 text-center">
              <Button
                size="lg"
                onClick={() => setStep(2)}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-lg py-6"
                data-testid="button-continue"
              >
                Continue with {selectedTierData.displayName || selectedTierData.name} Plan
              </Button>
              <p className="text-sm text-muted-foreground mt-4">
                Need help choosing? <a href="mailto:admin@entertaincrm.com" className="text-blue-500 hover:underline">Contact us</a>
              </p>
            </div>
          ) : null}

          {/* Trust Indicators */}
          <div className="max-w-4xl mx-auto mt-16 text-center">
            <p className="text-sm text-muted-foreground mb-6">Trusted by entertainment professionals nationwide</p>
            <div className="flex flex-wrap justify-center gap-8 text-muted-foreground/60">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-sm">14-day free trial</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-sm">No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-sm">Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Step 2: Account Details */
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="w-full max-w-lg shadow-xl">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl">Create Your Workspace</CardTitle>
              <p className="text-muted-foreground">
                You selected the <span className="font-semibold text-foreground">{selectedTierData?.displayName || selectedTierData?.name}</span> plan
                {isAnnual ? ' (billed annually)' : ' (billed monthly)'}
              </p>
              <div className="mt-4 p-4 rounded-lg bg-muted/50">
                <div className="text-3xl font-bold">
                  ${isAnnual ? formatDollars(selectedTierData?.yearlyPrice) : formatDollars(selectedTierData?.monthlyPrice)}
                  <span className="text-base font-normal text-muted-foreground">
                    /{isAnnual ? 'year' : 'month'}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">
                    Workspace Name
                  </label>
                  <Input
                    value={workspaceName}
                    onChange={(e) => setWorkspaceName(e.target.value)}
                    placeholder="My Entertainment Business"
                    required
                    data-testid="input-workspace-name"
                    className="h-11"
                  />
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Your URL: clarik.io/w/<span className="font-medium">{workspaceName.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "your-workspace"}</span>
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1.5">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    data-testid="input-email"
                    className="h-11"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1.5">
                    Password
                  </label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a secure password"
                    required
                    data-testid="input-password"
                    className="h-11"
                  />
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Must be 12+ characters with uppercase, lowercase, number, and symbol
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5">
                    Coupon Code <span className="text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <Input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                    data-testid="input-coupon-code"
                    className="h-11"
                  />
                </div>
                
                {error && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <div className="pt-4 flex gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setStep(1)} 
                    data-testid="button-back"
                    className="h-11"
                    disabled={isLoading}
                  >
                    Back
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 h-11" 
                    data-testid="button-create-workspace"
                    disabled={isLoading}
                  >
                    {isLoading ? "Redirecting to Payment..." : "Continue to Payment"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
